import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Layers,
  Cog,
  BarChart3,
  Award,
  TrendingUp,
  Cpu,
  Store,
  Headphones,
  Smartphone,
  Users,
  Clock,
} from "lucide-react";
import { useHomeCta } from "../hooks/useHomeCta";
import { openAgent } from "../utils/openAgent";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";

const SALES_OVERVIEW_CARDS = [
  {
    icon: Layers,
    iconColor: "#2563EB",
    iconBg: "#DCEEFE",
    title: "Lead Management",
    desc: "Capture, quality and distribute lead s from multiple channels automatically.",
  },
  {
    icon: Cog,
    iconColor: "#9333EA",
    iconBg: "#F3E8FF",
    title: "Automated Follow-ups",
    desc: "AI-driven follow-ups across emial, whatsapp and other channels to keep prospects engaged.",
  },
  {
    icon: BarChart3,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    title: "Sales Visibility",
    desc: "Real-time pipeline tracking, team performance and revenue insights in one place.",
  },
];

const SALES_STACK_LAYERS = [
  {
    title: "Demand Forecasting Engine",
    bg: "#FBBF24",
    textColor: "#1A202C",
    role: "Top Management",
    roleIcon: Award,
  },
  {
    title: "Supply Chain Finance Automation",
    bg: "#F97316",
    textColor: "#FFFFFF",
    role: "Finance Team",
    roleIcon: TrendingUp,
  },
  {
    title: "Enterprise Resourse Planning (ERP)",
    bg: "#EF4444",
    textColor: "#FFFFFF",
    role: "Functional & Middle Management",
    roleIcon: Cpu,
  },
  {
    title: "B@B Portal + DMS",
    bg: "#9F1239",
    textColor: "#FFFFFF",
    role: "Distributors & Dealers",
    roleIcon: Store,
  },
  {
    title: "CRM + Customer Support",
    bg: "#7C3AED",
    textColor: "#FFFFFF",
    role: "Office Sales & Support Team",
    roleIcon: Headphones,
  },
  {
    title: "Business Influencer Apps",
    bg: "#4338CA",
    textColor: "#FFFFFF",
    role: "Business Influencers",
    roleIcon: Smartphone,
  },
  {
    title: "Field Sales Apps",
    bg: "#3B82F6",
    textColor: "#FFFFFF",
    role: "Field Sales Team",
    roleIcon: Users,
  },
];

const SALES_KEY_BENEFITS = [
  {
    title: "Lead Capture & Qualification",
    desc: "Automatically capture leads from websites, campaigns and channels, and qualify them with AI.",
    renderIcon: () => (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-[60px] h-[60px] shrink-0"
        style={{ width: "60px", height: "60px" }}
      >
        <path
          d="M12 14H48C49.2 14 50 15.2 49.3 16.3L35 34.5V47C35 48.1 34.1 49 33 49H27C25.9 49 25 48.1 25 47V34.5L10.7 16.3C10 15.2 10.8 14 12 14Z"
          stroke="#2563EB"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          x1="12"
          y1="14"
          x2="48"
          y2="14"
          stroke="#2563EB"
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: "Automated Follow-ups",
    desc: "AI-powered multi-channel follow-ups to ensure no opportunity is missed.",
    renderIcon: () => (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-[60px] h-[60px] shrink-0"
        style={{ width: "60px", height: "60px" }}
      >
        <path
          d="M33 8L15 32H30L27 52L45 28H30L33 8Z"
          stroke="#EA580C"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Pipeline Management",
    desc: "Track deals, stages and revenue with real-time pipeline visibility.",
    renderIcon: () => (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-[60px] h-[60px] shrink-0"
        style={{ width: "60px", height: "60px" }}
      >
        <path
          d="M30 11L48 21.5V42.5L30 53L12 42.5V21.5L30 11Z"
          stroke="#16A34A"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 21.5L30 32L48 21.5"
          stroke="#16A34A"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M30 32V53"
          stroke="#16A34A"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M21 16L39 26.5"
          stroke="#16A34A"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Sales Team Productivity",
    desc: "Automate routine tasks so your team can focus high-value activities.",
    renderIcon: () => (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-[60px] h-[60px] shrink-0"
        style={{ width: "60px", height: "60px" }}
      >
        <circle cx="30" cy="18" r="5" stroke="#7C3AED" strokeWidth="3.2" />
        <circle cx="16" cy="22" r="4.2" stroke="#7C3AED" strokeWidth="3" />
        <circle cx="44" cy="22" r="4.2" stroke="#7C3AED" strokeWidth="3" />
        <path
          d="M21 44V39.5C21 35.8 24 33 30 33C36 33 39 35.8 39 39.5V44"
          stroke="#7C3AED"
          strokeWidth="3.2"
          strokeLinecap="round"
        />
        <path
          d="M10 44V41.5C10 38.5 12.5 36 16 36C18.2 36 20.1 37.1 21.2 38.8"
          stroke="#7C3AED"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M38.8 38.8C39.9 37.1 41.8 36 44 36C47.5 36 50 38.5 50 41.5V44"
          stroke="#7C3AED"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: "Customer Engagement",
    desc: "Personalized and timely communication across email, WhatsApp and other channels.",
    renderIcon: () => (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-[60px] h-[60px] shrink-0"
        style={{ width: "60px", height: "60px" }}
      >
        <path
          d="M30 48C30 48 12 37 12 24.5C12 18 17 13 23.5 13C27.2 13 29.2 15.2 30 16.2C30.8 15.2 32.8 13 36.5 13C43 13 48 18 48 24.5C48 37 30 48 30 48Z"
          stroke="#C026D3"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Reports & Analytics",
    desc: "Data-driven insights to forecast revenue and improve sales performance.",
    renderIcon: () => (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-[60px] h-[60px] shrink-0"
        style={{ width: "60px", height: "60px" }}
      >
        <rect
          x="12"
          y="29"
          width="9.5"
          height="19"
          rx="4.75"
          stroke="#CA8A04"
          strokeWidth="3.2"
        />
        <rect
          x="25.2"
          y="20"
          width="9.5"
          height="28"
          rx="4.75"
          stroke="#CA8A04"
          strokeWidth="3.2"
        />
        <rect
          x="38.5"
          y="11"
          width="9.5"
          height="37"
          rx="4.75"
          stroke="#CA8A04"
          strokeWidth="3.2"
        />
      </svg>
    ),
  },
];

const SALES_IMPACT_STATS = [
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#E0F7FA] border border-[#BAE6FD]">
        <BarChart3 className="w-5 h-5 text-[#00B4D8]" strokeWidth={2.2} />
      </div>
    ),
    stat: "30–50%",
    label: "Higher conversion rates",
  },
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#F3E8FF] border border-[#E9D5FF]">
        <Clock className="w-5 h-5 text-[#A855F7]" strokeWidth={2.2} />
      </div>
    ),
    stat: "40-60%",
    label: "Reduction in sales cycle time",
  },
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#DBEAFE] border border-[#BFDBFE]">
        <Users className="w-5 h-5 text-[#3B82F6]" strokeWidth={2.2} />
      </div>
    ),
    stat: "2-3x",
    label: "Increase in sales productivity",
  },
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#FEF3C7] border border-[#FDE68A]">
        <Layers className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.2} />
      </div>
    ),
    stat: "20-35%",
    label: "Growth in revenue pipeline",
  },
];

const SALES_TREND_DATA = [
  { month: "Jan", height: "30%", color: "#1E3A8A" },
  { month: "Feb", height: "42%", color: "#1D4ED8" },
  { month: "Mar", height: "48%", color: "#2563EB" },
  { month: "Apr", height: "66%", color: "#3B82F6" },
  { month: "May", height: "82%", color: "#00B4D8" },
  { month: "Jun", height: "100%", color: "#00D2EE", glow: true },
];

const SALES_PARTNERS = [
  {
    name: "OpenAI",
    img: "/images/labs/openai.png",
    width: "134px",
    height: "40px",
  },
  {
    name: "AWS",
    img: "/images/labs/aws.png",
    width: "51px",
    height: "30px",
  },
  {
    name: "NVIDIA",
    img: "/images/labs/nvidia.png",
    width: "164px",
    height: "30px",
  },
  {
    name: "Microsoft Azure",
    img: "/images/labs/azure.png",
    width: "215.88px",
    height: "30px",
    gap: "7.75px",
    flow: "horizontal",
  },
  {
    name: "Meta",
    img: "/images/labs/meta.png",
    width: "135px",
    height: "30px",
  },
  {
    name: "Anthropic",
    img: "/images/labs/Anthropic.png",
    width: "87px",
    height: "16px",
    flow: "vertical",
  },
];

const SalesStackPage = () => {
  const navigate = useNavigate();
  const goToHomeCta = useHomeCta();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#020B16] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black">
      {/* Stack Navbar matching screenshot */}
      <StackNavbar />

      {/* Main Hero Section with Background Video */}
      <section className="relative w-full min-h-screen flex items-center overflow-hidden pt-[76px] sm:pt-[82px]">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-full object-cover object-center"
          >
            <source src="/videos/sales-stack-hero.mp4" type="video/mp4" />
          </video>

          {/* Left-to-right dark gradient overlay matching screenshot */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #020B16 0%, rgba(2, 11, 22, 0.98) 32%, rgba(2, 11, 22, 0.88) 46%, rgba(2, 11, 22, 0.35) 68%, rgba(2, 11, 22, 0.08) 85%, transparent 100%)",
            }}
          />

          {/* Top and Bottom soft dark fades */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(2, 11, 22, 0.5) 0%, transparent 20%, transparent 80%, rgba(2, 11, 22, 0.7) 100%)",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-20 lg:px-[70px]">
          <div className="max-w-[680px] flex flex-col items-start">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[10.5px] sm:text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#7E93AC] select-none">
              <Link to="/what-we-do" className="hover:text-white transition-colors">
                WHAT WE DO
              </Link>
              <span>&gt;</span>
              <Link to="/what-we-do#stacks" className="hover:text-white transition-colors">
                STACK
              </Link>
              <span>&gt;</span>
              <span className="text-[#A2B6CE]">SALES AUTOMATION STACK</span>
            </div>

            {/* Main Heading */}
            <h1 className="mt-5 text-[38px] sm:text-[46px] lg:text-[52px] xl:text-[56px] font-extrabold leading-[1.08] tracking-tight">
              <span className="whitespace-nowrap">
                <span className="text-white">Sales </span>
                <span className="text-[#00D2EE]">Automation </span>
                <span className="text-[#3B82F6]">Stacks</span>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="mt-5 text-[16px] sm:text-[18px] font-bold text-white leading-snug">
              Intelligent automation to accelerate your sales growth.
            </p>

            {/* Description */}
            <p className="mt-4 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#94A3B8] max-w-[520px]">
              Unify your sales pipeline, CRM, customer data, and communication channels with
              AI-powered automation stacks. Accelerate lead conversions, eliminate manual follow-ups,
              and drive predictable revenue growth across every customer touchpoint.
            </p>

            {/* CTA Button */}
            <div className="mt-8">
              <button
                onClick={openAgent}
                className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-[14.5px] font-semibold text-[#0F172A] shadow-[0_0_35px_rgba(0,180,255,0.45)] transition-all duration-300 hover:bg-slate-100 hover:shadow-[0_0_45px_rgba(0,180,255,0.65)] hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>Talk to our team</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Overview Section matching screenshot */}
      <section
        className="w-full py-16 sm:py-20 lg:py-24 border-b border-[#E2E8F0]"
        style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-14">
            {/* Left Content */}
            <div className="flex flex-col items-start max-w-[560px]">
              <p className="text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
                PLATFORM OVERVIEW
              </p>

              <h2 className="mt-4 text-[32px] sm:text-[38px] lg:text-[42px] font-extrabold leading-[1.14] tracking-tight text-[#0F172A]">
                <span className="block sm:whitespace-nowrap">Transform Your Sales with</span>
                <span className="block bg-gradient-to-r from-[#00D2EE] to-[#0284C7] bg-clip-text text-transparent">
                  Intelligent Automation
                </span>
              </h2>

              <p className="mt-4 text-[14px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#64748B] max-w-[500px]">
                Our sales Automation Stacks connect your people, processes and data to drive higher conversations, shorter sales cycles, and predictable revenue growth across all channels.
              </p>
            </div>

            {/* Right Cards: 3 vertical cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
              {SALES_OVERVIEW_CARDS.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className="flex flex-col items-center text-center w-full sm:w-[224px] min-h-[255px] rounded-[14px] p-5 sm:p-6 bg-white transition-all duration-300 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-1 select-none"
                    style={{
                      border: "1px solid rgba(230, 230, 230, 1)",
                      gap: "16px",
                    }}
                  >
                    {/* Icon Badge */}
                    <div
                      className="flex h-[48px] w-[48px] items-center justify-center rounded-full shrink-0"
                      style={{ backgroundColor: card.iconBg }}
                    >
                      <Icon
                        className="h-5 w-5"
                        style={{ color: card.iconColor }}
                        strokeWidth={2.2}
                      />
                    </div>

                    {/* Card Title */}
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] leading-snug">
                      {card.title}
                    </h3>

                    {/* Card Description */}
                    <p className="text-[12px] sm:text-[12.5px] leading-[19px] sm:leading-[20px] text-[#64748B]">
                      {card.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Transforming Sales Operations Section matching Screenshot */}
      <section className="relative w-full overflow-hidden py-20 sm:py-24 lg:py-28 bg-[#000814]">
        {/* Background Image provided by user */}
        <img
          src="/images/what-we-do/sales-operations-bg.png"
          alt="Transforming Sales Operations Background"
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-center pointer-events-none select-none opacity-85"
        />

        {/* Subtle dark gradient overlay for optimal text contrast */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(0, 8, 20, 0.95) 0%, rgba(0, 8, 20, 0.78) 35%, rgba(0, 8, 20, 0.3) 65%, rgba(0, 8, 20, 0.6) 100%)",
          }}
        />

        {/* Content Container */}
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          <div className="flex flex-col xl:flex-row items-center justify-between gap-12 xl:gap-14">
            
            {/* Left Headline */}
            <div className="flex flex-col items-start max-w-[540px] shrink-0">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.6px] text-[#7E93AC]">
                TRANSFORMING SALES OPERATIONS
              </p>

              <h2 className="mt-4 text-[32px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.15] tracking-tight text-white">
                <span className="block sm:whitespace-nowrap">From leads to revenue,</span>
                <span className="block">end-to-end.</span>
              </h2>

              <p className="mt-4 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#9FB3C8] max-w-[480px]">
                Our Sales Automation Stacks integrates with CRM, communicates tools, and enterprise systems to create a connected sales ecosystem- from lead capture to deal closure.
              </p>
            </div>

            {/* Right Connected Architecture Diagram */}
            <div className="w-full xl:w-auto overflow-x-auto pb-4 xl:pb-0 flex justify-center">
              <div className="min-w-[620px] sm:min-w-[670px] flex items-center justify-center gap-2.5 sm:gap-3 py-4 select-none">
                
                {/* 1. Algorithm Layers Pillar (Cyan) */}
                <div
                  className="w-[28px] h-[292px] rounded-[8px] flex items-center justify-center shrink-0 shadow-md transition-transform duration-300 hover:scale-[1.02]"
                  style={{ backgroundColor: "#4ED0E5" }}
                >
                  <span
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    className="text-[11px] font-bold tracking-wider text-[#0B1528] whitespace-nowrap"
                  >
                    Algorithm Layers
                  </span>
                </div>

                {/* Dotted Arrow between Pillar 1 and Pillar 2 */}
                <div className="w-[18px] flex items-center justify-center shrink-0">
                  <svg className="w-4 h-3.5" viewBox="0 0 16 14" fill="none">
                    <line x1="2" y1="7" x2="14" y2="7" stroke="white" strokeWidth="1.2" strokeDasharray="1.5 1.5" strokeOpacity="0.8" />
                    <polyline points="5 4, 2 7, 5 10" stroke="white" strokeWidth="1.2" strokeOpacity="0.8" />
                    <polyline points="11 4, 14 7, 11 10" stroke="white" strokeWidth="1.2" strokeOpacity="0.8" />
                  </svg>
                </div>

                {/* 2. Unifies Data Platform Pillar (Lime Green) */}
                <div
                  className="w-[28px] h-[292px] rounded-[8px] flex items-center justify-center shrink-0 shadow-md transition-transform duration-300 hover:scale-[1.02]"
                  style={{ backgroundColor: "#BCE17C" }}
                >
                  <span
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    className="text-[11px] font-bold tracking-wider text-[#0B1528] whitespace-nowrap"
                  >
                    Unifies Data Platform
                  </span>
                </div>

                {/* Middle Column: 7 Stack Bars + Arrows */}
                <div className="flex flex-col gap-[9px] shrink-0">
                  {SALES_STACK_LAYERS.map((layer) => (
                    <div key={layer.title} className="flex items-center gap-2">
                      {/* Left bidirectional dashed arrow from Pillar 2 to Bar */}
                      <svg className="w-5 sm:w-6 h-3 shrink-0" viewBox="0 0 24 12" fill="none">
                        <line x1="2" y1="6" x2="22" y2="6" stroke="white" strokeWidth="1.2" strokeDasharray="1.5 1.5" strokeOpacity="0.75" />
                        <polyline points="5 3, 2 6, 5 9" stroke="white" strokeWidth="1.2" strokeOpacity="0.75" />
                        <polyline points="19 3, 22 6, 19 9" stroke="white" strokeWidth="1.2" strokeOpacity="0.75" />
                      </svg>

                      {/* Stack Bar Pill */}
                      <div
                        className="w-[200px] sm:w-[220px] h-[34px] rounded-[8px] px-3 flex items-center justify-center text-center text-[11px] sm:text-[11.5px] font-semibold leading-tight shadow-sm transition-all duration-300 hover:scale-[1.02] hover:brightness-105 shrink-0"
                        style={{ backgroundColor: layer.bg, color: layer.textColor }}
                      >
                        <span className="truncate">{layer.title}</span>
                      </div>

                      {/* Right curved dashed arrow from Bar to Role */}
                      <svg className="w-8 sm:w-10 h-4 shrink-0" viewBox="0 0 40 16" fill="none">
                        <path
                          d="M2 8 C15 8, 25 8, 36 8"
                          stroke="white"
                          strokeWidth="1.2"
                          strokeDasharray="2 2"
                          strokeOpacity="0.65"
                        />
                        <polyline
                          points="32 5, 36 8, 32 11"
                          stroke="white"
                          strokeWidth="1.2"
                          strokeOpacity="0.75"
                        />
                      </svg>

                      {/* Role with Icon */}
                      <div className="flex items-center gap-2.5 min-w-[190px] sm:min-w-[220px] shrink-0 text-left">
                        <layer.roleIcon className="w-4 h-4 text-white/90 shrink-0" strokeWidth={2} />
                        <span className="text-[12px] sm:text-[12.5px] font-medium text-white/90 tracking-wide">
                          {layer.role}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Key Benefits Section matching User Screenshot */}
      <section
        className="w-full py-16 sm:py-20 lg:py-24 border-b border-[#E2E8F0]"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: Title on Left, Description on Right */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 sm:mb-14">
            <div className="flex flex-col items-start max-w-[620px]">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                KEY BENEFITS
              </p>
              <h2 className="mt-3.5 text-[32px] sm:text-[40px] lg:text-[44px] font-extrabold leading-[1.14] tracking-tight text-[#0F172A]">
                Drive more conversions,<br />
                stronger relationships and<br />
                <span className="text-[#00D2EE]">greater revenue.</span>
              </h2>
            </div>

            <p className="text-[14px] sm:text-[14.5px] leading-[23px] text-[#64748B] max-w-[340px] pb-1">
              Discover the value that intelligent sales automation brings to your enterprise.
            </p>
          </div>

          {/* Cards Grid: 3 columns x 2 rows, with width: 60px, height: 60px icons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-[26px]">
            {SALES_KEY_BENEFITS.map((item) => (
              <div
                key={item.title}
                className="flex flex-col justify-start rounded-[20px] bg-white transition-all duration-300 hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] hover:-translate-y-1 select-none"
                style={{
                  border: "1px solid rgba(226, 232, 240, 1)",
                  padding: "28px 24px 30px",
                }}
              >
                {/* 60px x 60px Icon container */}
                <div className="flex items-center justify-start w-[60px] h-[60px]">
                  {item.renderIcon()}
                </div>

                {/* Card Title */}
                <h3 className="mt-6 text-[18px] sm:text-[19px] font-bold text-[#0F172A] leading-snug">
                  {item.title}
                </h3>

                {/* Card Description */}
                <p className="mt-3 text-[13.5px] sm:text-[14px] leading-[22px] text-[#64748B]">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Business Impact Section matching Screenshot */}
      <section
        className="w-full py-16 sm:py-20 lg:py-[96px] border-t"
        style={{
          backgroundColor: "rgba(236, 245, 253, 1)",
          borderTopColor: "rgba(241, 245, 249, 1)",
          borderTopWidth: "1px",
        }}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: Title on Left, Description on Right */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 lg:mb-14">
            <div className="flex flex-col items-start">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                REAL BUSINESS IMPACT
              </p>
              <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[40px] font-extrabold leading-[1.18] tracking-tight text-[#0F172A]">
                <span className="block whitespace-normal sm:whitespace-nowrap">
                  Measurable results for <span className="text-[#2563EB]">high-</span>
                </span>
                <span className="block text-[#00C2FF]">
                  performing sales teams.
                </span>
              </h2>
            </div>

            <div className="border-l border-[#CBD5E1] pl-5 sm:pl-6 py-1 md:mb-1.5 shrink-0">
              <p className="text-[13.5px] sm:text-[14px] leading-[21px] sm:leading-[22px] text-[#64748B] whitespace-normal sm:whitespace-nowrap">
                Our sales automation stacks help businesses close more<br className="hidden sm:block" />
                deals, reduce effort and achieve predictive growth.
              </p>
            </div>
          </div>

          {/* Cards Grid: 4 horizontal cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {SALES_IMPACT_STATS.map((item) => (
              <div
                key={item.label}
                className="w-full flex flex-row items-center bg-white transition-all duration-300 hover:shadow-[0_10px_25px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 select-none"
                style={{
                  minHeight: "98px",
                  borderRadius: "16px",
                  border: "1px solid rgba(218, 218, 218, 1)",
                  padding: "24px",
                  gap: "16px",
                  backgroundColor: "rgba(255, 255, 255, 1)",
                }}
              >
                {/* Icon Badge */}
                {item.icon}

                {/* Stat + Label */}
                <div className="flex flex-col justify-center min-w-0">
                  <div className="text-[22px] sm:text-[24px] font-extrabold text-[#0F172A] tracking-tight leading-none">
                    {item.stat}
                  </div>
                  <div className="text-[12px] sm:text-[12.5px] text-[#64748B] font-medium leading-tight mt-1 whitespace-nowrap">
                    {item.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Story Section matching Screenshots 1-5 */}
      <section
        className="w-full border-t border-white/[0.06]"
        style={{
          backgroundColor: "rgba(5, 12, 24, 1)",
          paddingTop: "100px",
          paddingBottom: "100px",
          paddingLeft: "clamp(24px, 5vw, 63px)",
          paddingRight: "clamp(24px, 5vw, 63px)",
        }}
      >
        <div className="mx-auto max-w-[1314px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-[48px] items-center min-h-[370px]">
            {/* Story Left Details (Col 1-6) */}
            <div className="lg:col-span-6 flex flex-col items-start max-w-[633px] gap-6">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
                SUCCESS STORY
              </p>

              <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] xl:text-[42px] font-extrabold text-white leading-tight tracking-tight whitespace-normal lg:whitespace-nowrap">
                Scaling Sales with Automation
              </h2>

              <p className="text-[14.5px] sm:text-[15px] leading-[26px] text-[#94A3B8]">
                A leading industrial solutions provider partnered with Simpel to automate their lead management, follow-ups and pipeline tracking. The result was higher conversion rate, faster deal closures and improved visibility across teams.
              </p>
            </div>

            {/* Story Analytics Overlay Right (Col 7-12) */}
            <div className="lg:col-span-6 flex justify-center lg:justify-end">
              <div
                className="w-full max-w-[633px] min-h-[370px] rounded-[24px] p-6 sm:p-7 flex flex-col justify-between select-none"
                style={{
                  backgroundColor: "rgba(22, 48, 109, 0.75)",
                  border: "1px solid rgba(51, 65, 85, 0.6)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  boxShadow: "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",
                }}
              >
                {/* Top Row: 4 Metric Cards */}
                <div className="grid grid-cols-4 gap-2 sm:gap-4 pb-4">
                  {/* Leads */}
                  <div className="flex flex-col">
                    <span className="text-[11px] sm:text-[12px] text-[#94A3B8] font-medium">Leads</span>
                    <span className="text-[18px] sm:text-[22px] font-bold text-white mt-1 leading-none">
                      2,450
                    </span>
                    <span className="text-[10.5px] sm:text-[11px] text-[#10B981] font-semibold mt-1.5 flex items-center gap-1">
                      <span className="text-[9px]">▲</span> 28%
                    </span>
                  </div>

                  {/* Opportunities */}
                  <div className="flex flex-col">
                    <span className="text-[11px] sm:text-[12px] text-[#94A3B8] font-medium">Opportunities</span>
                    <span className="text-[18px] sm:text-[22px] font-bold text-white mt-1 leading-none">
                      850
                    </span>
                    <span className="text-[10.5px] sm:text-[11px] text-[#10B981] font-semibold mt-1.5 flex items-center gap-1">
                      <span className="text-[9px]">▲</span> 32%
                    </span>
                  </div>

                  {/* Closed Deals */}
                  <div className="flex flex-col">
                    <span className="text-[11px] sm:text-[12px] text-[#94A3B8] font-medium">Closed Deals</span>
                    <span className="text-[18px] sm:text-[22px] font-bold text-white mt-1 leading-none">
                      320
                    </span>
                    <span className="text-[10.5px] sm:text-[11px] text-[#10B981] font-semibold mt-1.5 flex items-center gap-1">
                      <span className="text-[9px]">▲</span> 41%
                    </span>
                  </div>

                  {/* Revenue */}
                  <div className="flex flex-col">
                    <span className="text-[11px] sm:text-[12px] text-[#94A3B8] font-medium">Revenue</span>
                    <span className="text-[18px] sm:text-[22px] font-bold text-[#00D2EE] mt-1 leading-none">
                      ₹ 12.4 <span className="text-[12.5px] sm:text-[13px] font-medium text-white/90">Cr</span>
                    </span>
                    <span className="text-[10.5px] sm:text-[11px] text-[#10B981] font-semibold mt-1.5 flex items-center gap-1">
                      <span className="text-[9px]">▲</span> 31%
                    </span>
                  </div>
                </div>

                {/* Bottom Row: Sales Trend (Left) + Target Achievement (Right) */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-end justify-between gap-4 pt-3">
                  {/* Left: Sales Trend Bars */}
                  <div className="flex-1 flex flex-col justify-between h-[180px] sm:h-[195px] pr-0 sm:pr-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#94A3B8] font-medium text-[12px]">Sales Trend</span>
                      <span className="text-[#00D2EE] font-semibold text-[11px] tracking-wide">
                        H1 Performance
                      </span>
                    </div>

                    {/* Bars Container with Gap: 8px */}
                    <div className="flex items-end justify-between h-[140px] pt-2" style={{ gap: "8px" }}>
                      {SALES_TREND_DATA.map((item) => (
                        <div key={item.month} className="flex flex-col items-center flex-1 h-full justify-end">
                          <div
                            className={`w-[55px] max-w-full rounded-t-[6px] transition-all duration-300 hover:brightness-110 ${
                              item.glow ? "shadow-[0_0_18px_rgba(0,210,238,0.65)]" : ""
                            }`}
                            style={{
                              height: item.height,
                              backgroundColor: item.color,
                              width: "55px",
                            }}
                          />
                          <span className="mt-2 text-[10.5px] sm:text-[11px] text-[#94A3B8] font-medium text-center">
                            {item.month}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Target Achievement Card */}
                  <div
                    className="w-full sm:w-[155px] h-[180px] sm:h-[195px] rounded-[16px] p-3.5 flex flex-col items-center justify-between shrink-0"
                    style={{
                      backgroundColor: "rgba(9, 21, 46, 0.85)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                    }}
                  >
                    <span className="text-[#94A3B8] text-[11px] font-medium text-center">
                      Target Achievement
                    </span>

                    {/* Circular Progress Gauge */}
                    <div className="relative flex items-center justify-center my-1">
                      <svg width="78" height="78" viewBox="0 0 78 78" className="w-[78px] h-[78px]">
                        {/* Background track */}
                        <circle
                          cx="39"
                          cy="39"
                          r="31"
                          fill="none"
                          stroke="#162846"
                          strokeWidth="6"
                        />
                        {/* Progress ring 72% */}
                        <circle
                          cx="39"
                          cy="39"
                          r="31"
                          fill="none"
                          stroke="#00D2EE"
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={194.78}
                          strokeDashoffset={194.78 * (1 - 0.72)}
                          transform="rotate(-90 39 39)"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[17px] font-extrabold text-white">72%</span>
                      </div>
                    </div>

                    <span className="text-[#00D2EE] text-[11px] font-semibold tracking-wide">
                      On Track
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Manufacturing Leaders Section with Marquee */}
      <style>{`
        @keyframes marqueeSales {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee-sales {
          display: flex;
          width: max-content;
          animation: marqueeSales 22s linear infinite;
        }
        .animate-marquee-sales:hover {
          animation-play-state: paused;
        }
      `}</style>
      <section className="w-full bg-white py-16 sm:py-20 lg:py-24 border-t border-[#F1F5F9] overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px] text-center mb-12 sm:mb-14">
          <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-extrabold text-[#0F172A] tracking-tight">
            Trusted by Manufacturing Leaders
          </h2>
          <p className="mt-3 text-[14px] sm:text-[15px] text-[#64748B] max-w-[560px] mx-auto">
            Our clients achieve measurable improvements and scalable growth.
          </p>
        </div>

        {/* Marquee Row */}
        <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
          <div className="animate-marquee-sales items-center py-2">
            {[...SALES_PARTNERS, ...SALES_PARTNERS, ...SALES_PARTNERS, ...SALES_PARTNERS].map((partner, idx) => (
              <div
                key={`${partner.name}-${idx}`}
                className={`flex shrink-0 items-center justify-center px-8 sm:px-10 lg:px-12 select-none ${
                  partner.flow === "vertical" ? "flex-col" : "flex-row"
                }`}
                style={partner.gap ? { gap: partner.gap } : undefined}
              >
                <img
                  src={partner.img}
                  alt={partner.name}
                  style={{
                    width: partner.width,
                    height: partner.height,
                  }}
                  className="object-contain opacity-90 transition-all duration-300 hover:opacity-100 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Transform CTA Section matching Screenshot */}
      <section className="relative w-full overflow-hidden bg-[#020A15] py-20 sm:py-24 lg:py-28 min-h-[440px] flex items-center">
        {/* Background Earth Glow Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/what-we-do/manufacturing-cta-earth.png"
            alt="Ready to transform your manufacturing"
            className="w-full h-full object-cover object-[70%_center] lg:object-right select-none pointer-events-none"
          />
          {/* Subtle mobile/tablet readability vignette */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#020A15]/85 via-[#020A15]/50 to-transparent lg:hidden" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          <div className="flex flex-col lg:flex-row items-start justify-between gap-10">
            {/* Left Content */}
            <div className="max-w-[680px]">
              {/* Eyebrow */}
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
                READY TO TRANSFORM YOUR MANUFACTURING
              </p>

              {/* Heading */}
              <h2 className="mt-4 text-[34px] sm:text-[42px] lg:text-[46px] font-extrabold text-white leading-[1.15] tracking-tight">
                <span className="block sm:whitespace-nowrap">Let’s build an intelligent factory,</span>
                <span className="block">together.</span>
              </h2>

              {/* Paragraph */}
              <p className="mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#94A3B8] max-w-[480px]">
                Talk to our team to explore how our Manufacturing Automation Stacks can help your business grow.
              </p>

              {/* CTA Button */}
              <div className="mt-8">
                <button
                  onClick={openAgent}
                  className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-[14.5px] font-semibold text-[#0F172A] shadow-[0_0_35px_rgba(0,180,255,0.45)] transition-all duration-300 hover:bg-slate-100 hover:shadow-[0_0_45px_rgba(0,180,255,0.65)] hover:scale-[1.02] active:scale-[0.98]"
                >
                  <span>Talk to Our Team</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>

            {/* Right Side: BOLDER BRIGHTER TOGETHER Tag */}
            <div className="hidden lg:flex flex-col items-center select-none pt-2">
              <div className="flex flex-col items-start gap-1 text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[3px] text-white/70">
                <span>BOLDER</span>
                <span>BRIGHTER</span>
                <span>TOGETHER</span>
              </div>
              <div className="w-[1px] h-[54px] bg-white/20 mt-4 self-center" />
            </div>
          </div>
        </div>
      </section>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default SalesStackPage;
